import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  BookOpen, ChevronLeft, ChevronRight, ListChecks, RefreshCw, Repeat,
  PiggyBank, Upload, Plus, X, Info, ArrowUpRight, ArrowDownRight, TrendingUp,
  Receipt, Users,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import DetailDrawer from "@/components/DetailDrawer";
import { fmtCents, mondayOf, addDays, isoDate } from "@/lib/time";
import { toast } from "sonner";
import { Skeleton, SkeletonTable, SkeletonCard } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";
import InvoicesPanel from "@/components/books/InvoicesPanel";
import CustomersPanel from "@/components/books/CustomersPanel";

const CATEGORIES = [
  "food_cost", "drink_cost", "rent", "utilities", "supplies",
  "software", "wages_external", "marketing", "other",
];

export default function Books() {
  const [params, setParams] = useSearchParams();
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("books.manage");
  const tab = params.get("tab") || "margin";
  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1400px]" data-testid="books-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center">
          <BookOpen size={17}/>
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Books</div>
          <div className="text-[11.5px] text-brand-muted">Categorised spend, recurring, weekly takings, per-site net margin, invoicing-lite. Not tax.</div>
        </div>
      </div>
      <div className="flex items-center gap-1 border-b border-brand-rule mb-4 overflow-x-auto">
        <Tab v="margin"       cur={tab} setV={(v)=>setParams({tab:v})} icon={<TrendingUp size={13}/>}>Margin</Tab>
        <Tab v="transactions" cur={tab} setV={(v)=>setParams({tab:v})} icon={<ListChecks size={13}/>}>Transactions</Tab>
        <Tab v="invoices"     cur={tab} setV={(v)=>setParams({tab:v})} icon={<Receipt size={13}/>}>Invoices</Tab>
        <Tab v="customers"    cur={tab} setV={(v)=>setParams({tab:v})} icon={<Users size={13}/>}>Customers</Tab>
        <Tab v="recurring"    cur={tab} setV={(v)=>setParams({tab:v})} icon={<Repeat size={13}/>}>Recurring</Tab>
        <Tab v="takings"      cur={tab} setV={(v)=>setParams({tab:v})} icon={<PiggyBank size={13}/>}>Takings</Tab>
      </div>
      {tab === "margin" && <MarginTab/>}
      {tab === "transactions" && <TransactionsTab/>}
      {tab === "invoices" && <InvoicesPanel companyId={companyId} canManage={canManage}/>}
      {tab === "customers" && <CustomersPanel companyId={companyId} canManage={canManage}/>}
      {tab === "recurring" && <RecurringTab/>}
      {tab === "takings" && <TakingsTab/>}
    </div>
  );
}

function Tab({ v, cur, setV, children, icon }) {
  const active = v === cur;
  return (
    <button onClick={() => setV(v)}
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 flex items-center gap-1.5 ${active ? 'border-brand-violet text-brand-violet' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}
            data-testid={`books-tab-${v}`}>
      {icon}{children}
    </button>
  );
}

// ---------------- MARGIN ----------------

function MarginTab() {
  const { companyId } = useParams();
  const [weekMonday, setWeekMonday] = React.useState(mondayOf());
  const [data, setData] = React.useState(null);
  const [drill, setDrill] = React.useState(null); // {siteName, kind, cents, txs?}
  const weekStartIso = isoDate(weekMonday);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/books/margin/week`, {
        params: { week_start: weekStartIso },
      });
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId, weekStartIso]);
  React.useEffect(() => { load(); }, [load]);

  async function openDrill(kind, site) {
    setDrill({ loading: true, kind, site });
    try {
      const start = weekStartIso;
      const end = isoDate(addDays(weekMonday, 7));
      if (kind === "card_spend") {
        const { data } = await api.get(`/companies/${companyId}/books/transactions`, {
          params: { start, end, site_id: site.site_id },
        });
        // Only negative amounts
        const spends = data.transactions.filter(t => t.amount_cents < 0);
        setDrill({ kind, site, txs: spends });
      } else if (kind === "bookings_revenue") {
        // Fetch bookings for this site + week
        const startDt = weekMonday.toISOString();
        const endDt = addDays(weekMonday, 7).toISOString();
        const { data } = await api.get(`/companies/${companyId}/bookings`, {
          params: { site_id: site.site_id, start: startDt, end: endDt },
        });
        const bkg = data.bookings.filter(b => b.status === "confirmed" || b.status === "completed");
        setDrill({ kind, site, bookings: bkg });
      } else if (kind === "labour") {
        const startDt = weekMonday.toISOString();
        const endDt = addDays(weekMonday, 7).toISOString();
        const { data } = await api.get(`/companies/${companyId}/rotas/shifts`, {
          params: { site_id: site.site_id, start: startDt, end: endDt },
        });
        const shifts = data.shifts.filter(s => s.status === "published" && s.member_id);
        setDrill({ kind, site, shifts });
      } else {
        setDrill({ kind, site });
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); setDrill(null); }
  }

  if (!data) return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="books-margin-loading">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );

  const netToneCents = (n) => n > 0 ? "text-emerald-700" : n < 0 ? "text-brand-coral" : "text-brand-muted";

  return (
    <div data-testid="books-margin">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-1 rounded-md border border-brand-rule bg-card">
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, -7))}
                  data-testid="margin-prev-week"><ChevronLeft size={14}/></button>
          <button className="btn-ghost !h-8 !px-2 mono-num text-[11px]"
                  onClick={() => setWeekMonday(mondayOf())}
                  data-testid="margin-today">
            Week of {weekStartIso}
          </button>
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, 7))}
                  data-testid="margin-next-week"><ChevronRight size={14}/></button>
        </div>
        <div className="flex-1"/>
        <div className="text-[11px] text-brand-muted flex items-center gap-1">
          <Info size={12}/> Click any figure for a breakdown
        </div>
      </div>

      {/* Totals strip */}
      <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="margin-totals">
        <SumTile label="Takings"  value={fmtCents(data.totals.takings_cents)}/>
        <SumTile label="Bookings rev" value={fmtCents(data.totals.bookings_revenue_cents)}/>
        <SumTile label="Card spend" value={fmtCents(data.totals.card_spend_cents)} tone="coral"/>
        <SumTile label="Net margin" value={fmtCents(data.totals.net_margin_cents)}
                 tone={data.totals.net_margin_cents >= 0 ? "emerald" : "coral"} big/>
      </div>

      {/* Per-site table */}
      <div className="card-panel overflow-hidden">
        <div className="table-scroll">
          <table className="fable-table" data-testid="margin-table">
            <thead>
              <tr>
                <th style={{width:"18%"}}>Site</th>
                <th className="text-right">Takings</th>
                <th className="text-right">Bookings rev</th>
                <th className="text-right">Card spend</th>
                <th className="text-right">Labour</th>
                <th className="text-right">Net margin</th>
              </tr>
            </thead>
            <tbody>
              {data.sites.map((s) => (
                <tr key={s.site_id} data-testid={`margin-row-${s.site_id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{background: s.site_color || '#6E3CFA'}}/>
                      <span className="text-[12.5px] font-medium text-brand-ink">{s.site_name}</span>
                    </div>
                  </td>
                  <td className="mono-num text-right">
                    <button className="hover:underline" onClick={() => openDrill("takings", s)}
                            data-testid={`margin-takings-${s.site_id}`}>
                      {fmtCents(s.takings_cents)}
                    </button>
                  </td>
                  <td className="mono-num text-right">
                    <button className="hover:underline" onClick={() => openDrill("bookings_revenue", s)}
                            data-testid={`margin-bookings-${s.site_id}`}>
                      {fmtCents(s.bookings_revenue_cents)}
                      {s.bookings_count > 0 && <span className="text-brand-muted text-[10px] ml-1">({s.bookings_count})</span>}
                    </button>
                  </td>
                  <td className="mono-num text-right text-brand-coral">
                    <button className="hover:underline" onClick={() => openDrill("card_spend", s)}
                            data-testid={`margin-spend-${s.site_id}`}>
                      −{fmtCents(s.card_spend_cents)}
                      {s.card_spend_count > 0 && <span className="text-brand-faint text-[10px] ml-1">({s.card_spend_count})</span>}
                    </button>
                  </td>
                  <td className="mono-num text-right text-brand-muted">
                    <button className="hover:underline" onClick={() => openDrill("labour", s)}
                            data-testid={`margin-labour-${s.site_id}`}>
                      −{fmtCents(s.labour_cents)}
                    </button>
                  </td>
                  <td className={`mono-num text-right font-semibold ${netToneCents(s.net_margin_cents)}`}>
                    {s.net_margin_cents >= 0 ? "" : "−"}
                    {fmtCents(Math.abs(s.net_margin_cents))}
                  </td>
                </tr>
              ))}
              <tr className="bg-brand-hush">
                <td className="font-semibold">Total</td>
                <td className="mono-num text-right font-semibold">{fmtCents(data.totals.takings_cents)}</td>
                <td className="mono-num text-right font-semibold">{fmtCents(data.totals.bookings_revenue_cents)}</td>
                <td className="mono-num text-right font-semibold text-brand-coral">−{fmtCents(data.totals.card_spend_cents)}</td>
                <td className="mono-num text-right font-semibold text-brand-muted">−{fmtCents(data.totals.labour_cents)}</td>
                <td className={`mono-num text-right font-bold ${netToneCents(data.totals.net_margin_cents)}`}>
                  {data.totals.net_margin_cents >= 0 ? "" : "−"}
                  {fmtCents(Math.abs(data.totals.net_margin_cents))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {data.unassigned_card_spend_cents > 0 && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2"
             data-testid="margin-unassigned">
          <Info size={13} className="text-amber-700"/>
          <div className="text-[12px] text-amber-800">
            <span className="mono-num font-semibold">{fmtCents(data.unassigned_card_spend_cents)}</span> in
            card spend has no site assigned ({data.unassigned_card_spend_count} transactions). Assign them in
            <b> Transactions</b> to pull them into the site rows.
          </div>
        </div>
      )}

      <DetailDrawer open={!!drill} onOpenChange={(v)=>{if(!v) setDrill(null);}}
                    title={drill ? `${drill.site.site_name} — ${drill.kind.replace('_',' ')}` : ""}
                    subtitle={`Week of ${weekStartIso}`}
                    testid="margin-drill">
        {drill && <DrillBody drill={drill} weekStartIso={weekStartIso}/>}
      </DetailDrawer>
    </div>
  );
}

function DrillBody({ drill }) {
  if (drill.kind === "card_spend") {
    return (
      <div className="p-5">
        <div className="micro-label mb-2">{drill.txs?.length || 0} transactions</div>
        <div className="card-panel overflow-hidden">
          <div className="table-scroll">
            <table className="fable-table">
              <thead>
                <tr><th style={{width:"22%"}}>Date</th><th>Description</th><th className="text-right">Amount</th></tr>
              </thead>
              <tbody>
                {drill.txs?.map((t) => (
                  <tr key={t.id}>
                    <td className="mono-num text-[11.5px]">{t.date}</td>
                    <td className="text-[12px]">{t.description} <span className="chip mono-num text-[10px] ml-1">{t.category || 'review'}</span></td>
                    <td className="mono-num text-right text-brand-coral">−{fmtCents(Math.abs(t.amount_cents))}</td>
                  </tr>
                ))}
                {(drill.txs || []).length === 0 && (
                  <tr><td colSpan={3} className="text-center py-6 text-[12px] text-brand-muted">No transactions</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
  if (drill.kind === "bookings_revenue") {
    return (
      <div className="p-5">
        <div className="micro-label mb-2">{drill.bookings?.length || 0} confirmed / completed bookings</div>
        <div className="card-panel overflow-hidden">
          <div className="table-scroll">
            <table className="fable-table">
              <thead>
                <tr><th style={{width:"26%"}}>When</th><th>Service</th><th>Customer</th><th className="text-right">Price</th></tr>
              </thead>
              <tbody>
                {drill.bookings?.map((b) => (
                  <tr key={b.id}>
                    <td className="mono-num text-[11.5px]">{b.starts_at.slice(0,10)} {b.starts_at.slice(11,16)}</td>
                    <td className="text-[12px]">{b.service_name}</td>
                    <td className="text-[12px]">{b.customer?.name}</td>
                    <td className="mono-num text-right">{fmtCents(b.service_price_cents)}</td>
                  </tr>
                ))}
                {(drill.bookings || []).length === 0 && (
                  <tr><td colSpan={4} className="text-center py-6 text-[12px] text-brand-muted">No bookings</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
  if (drill.kind === "labour") {
    const shifts = drill.shifts || [];
    return (
      <div className="p-5">
        <div className="micro-label mb-2">{shifts.length} published shifts (site + week)</div>
        <div className="card-panel overflow-hidden">
          <div className="table-scroll">
            <table className="fable-table">
              <thead>
                <tr><th>When</th><th>Position</th><th className="text-right">Duration</th></tr>
              </thead>
              <tbody>
                {shifts.map((s) => (
                  <tr key={s.id}>
                    <td className="mono-num text-[11.5px]">{s.start.slice(0,10)} {s.start.slice(11,16)}–{s.end.slice(11,16)}</td>
                    <td className="text-[12px]">{s.position || '—'}</td>
                    <td className="mono-num text-right">
                      {(((new Date(s.end) - new Date(s.start)) / 3600000) || 0).toFixed(1)}h
                    </td>
                  </tr>
                ))}
                {shifts.length === 0 && (
                  <tr><td colSpan={3} className="text-center py-6 text-[12px] text-brand-muted">No shifts</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-5 text-[12px] text-brand-muted">
      Weekly takings are manually entered on the <b>Takings</b> tab. Site total for this week:
      <span className="mono-num text-brand-ink ml-1">{fmtCents(drill.site.takings_cents)}</span>.
    </div>
  );
}

function SumTile({ label, value, tone, big }) {
  const toneClass = tone === "coral" ? "text-brand-coral" : tone === "emerald" ? "text-emerald-700" : "text-brand-ink";
  return (
    <div className="card-panel px-3 py-2">
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 mono-num ${big ? 'text-[18px] font-bold' : 'text-[15px] font-semibold'} ${toneClass}`}>
        {value}
      </div>
    </div>
  );
}

// ---------------- TRANSACTIONS ----------------

function TransactionsTab() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("books.manage");
  const [rows, setRows] = React.useState(null);
  const [sites, setSites] = React.useState([]);
  const [siteFilter, setSiteFilter] = React.useState("");
  const [catFilter, setCatFilter] = React.useState("");
  const [reviewOnly, setReviewOnly] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/books/transactions`, {
        params: {
          site_id: siteFilter || undefined,
          category: catFilter || undefined,
          review_only: reviewOnly,
          limit: 500,
        },
      });
      setRows(data.transactions);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId, siteFilter, catFilter, reviewOnly]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    api.get(`/companies/${companyId}/sites`).then(({data}) => setSites(data.sites));
  }, [companyId]);

  async function patch(tx, upd) {
    try {
      await api.patch(`/companies/${companyId}/books/transactions/${tx.id}`, upd);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  if (!rows) return (
    <div className="card-panel overflow-hidden" data-testid="tx-loading">
      <SkeletonTable rows={8} cols={6} />
    </div>
  );

  const reviewCount = rows.filter(t => !t.category).length;

  return (
    <div data-testid="books-transactions">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select className="field-input !h-8 !w-40" value={siteFilter}
                onChange={(e)=>setSiteFilter(e.target.value)}
                data-testid="tx-site-filter">
          <option value="">All sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          <option value="__none__" disabled>—</option>
        </select>
        <select className="field-input !h-8 !w-40" value={catFilter}
                onChange={(e)=>setCatFilter(e.target.value)}
                data-testid="tx-cat-filter">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-[12px] cursor-pointer" data-testid="tx-review-only">
          <input type="checkbox" checked={reviewOnly} onChange={(e)=>setReviewOnly(e.target.checked)}/>
          <span>Review queue{reviewCount > 0 && <span className="ml-1 chip chip-amber mono-num text-[10px]">{reviewCount}</span>}</span>
        </label>
        <div className="flex-1"/>
        {canManage && (
          <button className="btn-outline" onClick={() => setImportOpen(true)} data-testid="tx-import-open">
            <Upload size={13}/> Import CSV
          </button>
        )}
      </div>

      <div className="card-panel overflow-hidden">
        <div className="table-scroll">
          <table className="fable-table" data-testid="tx-table">
            <thead>
              <tr>
                <th style={{width:"12%"}}>Date</th>
                <th>Description</th>
                <th style={{width:"14%"}}>Category</th>
                <th style={{width:"14%"}}>Site</th>
                <th style={{width:"6%"}} className="text-center">Rec</th>
                <th style={{width:"12%"}} className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} data-testid={`tx-row-${t.id}`}>
                  <td className="mono-num text-[11.5px]">{t.date}</td>
                  <td>
                    <div className="text-[12.5px] text-brand-ink truncate max-w-[420px]">{t.description}</div>
                    <div className="mono-num text-[10.5px] text-brand-faint">
                      {t.account_label} · {t.source}
                    </div>
                  </td>
                  <td>
                    <select className="field-input !h-7 !w-32" value={t.category || ""}
                            onChange={(e)=>patch(t, { category: e.target.value || null })}
                            disabled={!canManage}
                            data-testid={`tx-cat-${t.id}`}>
                      <option value="">— review —</option>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {t.auto_categorised && t.category && (
                      <div className="mono-num text-[9.5px] text-brand-faint mt-0.5">auto</div>
                    )}
                  </td>
                  <td>
                    <select className="field-input !h-7 !w-32" value={t.site_id || ""}
                            onChange={(e)=>patch(t, { site_id: e.target.value || null })}
                            disabled={!canManage}
                            data-testid={`tx-site-${t.id}`}>
                      <option value="">— none —</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td className="text-center">
                    {t.recurring ? <span className="chip chip-violet mono-num text-[10px]">{t.recurring_cadence || "yes"}</span> : ""}
                  </td>
                  <td className={`mono-num text-right ${t.amount_cents < 0 ? 'text-brand-coral' : 'text-emerald-700 font-semibold'}`}>
                    {t.amount_cents < 0 ? '−' : ''}{fmtCents(Math.abs(t.amount_cents))}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-0">
                  <EmptyState icon={ListChecks} eyebrow="Transactions"
                    title="No transactions match these filters."
                    body="Try clearing filters, or import a CSV."
                    testid="tx-empty" />
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ImportDrawer open={importOpen} onOpenChange={setImportOpen} companyId={companyId} onDone={load}/>
    </div>
  );
}

function ImportDrawer({ open, onOpenChange, companyId, onDone }) {
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [report, setReport] = React.useState(null);

  async function submit() {
    if (!file || busy) return;
    setBusy(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/companies/${companyId}/books/transactions/import-csv`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      toast.success(`Imported ${data.imported}`);
      onDone && onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <DetailDrawer open={open} onOpenChange={onOpenChange}
                  title="Import bank CSV" subtitle="Columns: date, description, amount"
                  testid="tx-import-drawer">
      <div className="p-5 space-y-3">
        <div className="rounded-md border border-brand-rule bg-brand-canvas px-3 py-2 text-[11.5px] text-brand-muted">
          Bank feeds coming soon. Meanwhile, drop a CSV from your bank. Amounts negative = spend.
          Duplicates on <span className="mono-num">(date, description, amount)</span> are skipped.
          Merchant → category rules run automatically.
        </div>
        <input type="file" accept=".csv" onChange={(e)=>setFile(e.target.files?.[0])}
               data-testid="tx-import-file"/>
        <div>
          <button className="btn-violet" onClick={submit} disabled={!file || busy}
                  data-testid="tx-import-submit">
            <Upload size={13}/> {busy ? "Importing…" : "Import"}
          </button>
        </div>
        {report && (
          <div className="card-panel px-3 py-2 mt-2" data-testid="tx-import-report">
            <div className="micro-label mb-1">Report</div>
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label="Imported" v={report.imported}/>
              <MiniStat label="Duplicates" v={report.duplicates}/>
              <MiniStat label="Auto-cat" v={report.auto_categorised}/>
            </div>
            {report.review_queue?.length > 0 && (
              <div className="mt-2 text-[11.5px] text-brand-muted">
                <span className="mono-num">{report.review_queue.length}</span> new entries need
                categorising — filter Transactions → <b>Review queue</b>.
              </div>
            )}
          </div>
        )}
      </div>
    </DetailDrawer>
  );
}

function MiniStat({ label, v }) {
  return (
    <div className="rounded-md border border-brand-rule bg-card px-2 py-1.5">
      <div className="micro-label">{label}</div>
      <div className="mono-num text-[13px] font-semibold text-brand-ink mt-0.5">{v}</div>
    </div>
  );
}

// ---------------- RECURRING ----------------

function RecurringTab() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("books.manage");
  const [groups, setGroups] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/books/recurring`);
      setGroups(data.groups);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  async function refresh() {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/books/recurring/refresh`, {});
      toast.success(data.fresh_detected?.length > 0 ? `Detected ${data.fresh_detected.length}` : "No new patterns");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (!groups) return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="books-rec-loading">
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );

  return (
    <div data-testid="books-recurring">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] text-brand-muted">
          <span className="mono-num text-brand-ink">{groups.length}</span> recurring patterns
        </div>
        {canManage && (
          <button className="btn-outline" onClick={refresh} disabled={busy} data-testid="books-rec-refresh">
            <RefreshCw size={13}/> {busy ? "Refreshing…" : "Detect now"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {groups.map((g) => (
          <div key={g.merchant_key} className="card-panel p-3" data-testid={`rec-${g.merchant_key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-brand-ink truncate">{g.sample_description}</div>
                <div className="mono-num text-[10.5px] text-brand-muted">
                  {g.count} occurrences · latest {g.latest_date}
                </div>
              </div>
              <span className="chip chip-violet mono-num text-[10px]">{g.cadence || 'irregular'}</span>
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <div className="mono-num text-[15px] font-bold text-brand-coral">
                −{fmtCents(Math.abs(g.sample_amount_cents))}
              </div>
              <div className="mono-num text-[10.5px] text-brand-muted">approx / period</div>
            </div>
          </div>
        ))}
        {groups.length === 0 && (
          <div className="col-span-2">
            <EmptyState icon={Repeat} eyebrow="Recurring"
              title="No recurring patterns yet."
              body="Import a few weeks of transactions to see subscriptions and rents emerge."
              testid="books-rec-empty" />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- TAKINGS ----------------

function TakingsTab() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("books.manage");
  const [weekMonday, setWeekMonday] = React.useState(mondayOf());
  const [sites, setSites] = React.useState([]);
  const [rows, setRows] = React.useState({});
  const [saving, setSaving] = React.useState({});
  const weekStartIso = isoDate(weekMonday);

  const load = React.useCallback(async () => {
    const [{ data: ss }, { data: tk }] = await Promise.all([
      api.get(`/companies/${companyId}/sites`),
      api.get(`/companies/${companyId}/books/weekly-takings`, { params: { week_start: weekStartIso } }),
    ]);
    setSites(ss.sites);
    const map = {};
    for (const t of tk.weekly_takings) map[t.site_id] = t.amount_cents;
    setRows(map);
  }, [companyId, weekStartIso]);
  React.useEffect(() => { load(); }, [load]);

  async function save(site_id, amount_cents) {
    if (!canManage) return;
    setSaving(s => ({ ...s, [site_id]: true }));
    try {
      await api.put(`/companies/${companyId}/books/weekly-takings`, {
        site_id, week_start: weekStartIso, amount_cents: Number(amount_cents) || 0,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(s => ({ ...s, [site_id]: false })); }
  }

  const total = Object.values(rows).reduce((a, c) => a + (c || 0), 0);

  return (
    <div data-testid="books-takings">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1 rounded-md border border-brand-rule bg-card">
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, -7))}
                  data-testid="takings-prev-week"><ChevronLeft size={14}/></button>
          <button className="btn-ghost !h-8 !px-2 mono-num text-[11px]"
                  onClick={() => setWeekMonday(mondayOf())}
                  data-testid="takings-today">
            Week of {weekStartIso}
          </button>
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, 7))}
                  data-testid="takings-next-week"><ChevronRight size={14}/></button>
        </div>
        <div className="flex-1"/>
        <div className="text-[12px] text-brand-muted">
          Total: <span className="mono-num text-brand-ink font-semibold">{fmtCents(total)}</span>
        </div>
      </div>

      <div className="card-panel overflow-hidden">
        <div className="table-scroll">
          <table className="fable-table" data-testid="takings-table">
            <thead>
              <tr><th style={{width:"40%"}}>Site</th><th className="text-right">Takings (weekly)</th><th style={{width:"20%"}} className="text-right"></th></tr>
            </thead>
            <tbody>
              {sites.map((s) => {
                const cents = rows[s.id] ?? 0;
                return (
                  <tr key={s.id} data-testid={`takings-row-${s.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{background: s.color || '#6E3CFA'}}/>
                        <span className="text-[12.5px] font-medium text-brand-ink">{s.name}</span>
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1 mono-num">
                        <span className="text-brand-muted">£</span>
                        <input type="number" className="field-input !h-8 !w-36 mono-num text-right"
                               defaultValue={(cents / 100).toFixed(2)}
                               onBlur={(e) => {
                                 const v = Math.round(parseFloat(e.target.value || "0") * 100);
                                 if (v !== cents) save(s.id, v);
                               }}
                               disabled={!canManage}
                               data-testid={`takings-input-${s.id}`}/>
                      </div>
                    </td>
                    <td className="text-right mono-num text-[11px] text-brand-muted">
                      {saving[s.id] ? "saving…" : ""}
                    </td>
                  </tr>
                );
              })}
              {sites.length === 0 && (
                <tr><td colSpan={3} className="text-center py-8 text-[12px] text-brand-muted">
                  No sites configured.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 text-[11.5px] text-brand-muted flex items-start gap-2">
        <Info size={12} className="shrink-0 mt-0.5"/>
        <div>
          Enter your weekly card + cash takings per site here. Bookings revenue is tracked
          separately from the <b>Bookings</b> module and shown as its own column in the margin table.
        </div>
      </div>
    </div>
  );
}
